import React, { useEffect, useRef } from 'react';
import { createDitherScene, renderDither, sampleImage, stepField, DitherScene, HoverMode, Ripple, RIPPLE_LIFE, TRAIL_LIFE } from '../utils/ditherField';
const TRAIL_SPACING = 10; // px between touch points
const MAX_TRAIL = 40;
const RIPPLE_SPACING = 70; // px the cursor travels between rings
const RIPPLE_MIN_GAP = 160; // ms between rings
const MAX_RIPPLES = 10;
const IMAGE_URL = 'https://cdn.magicpatterns.com/patterns/generated-images/690da513-d9a5-4aae-86ef-d67d812188ee.jpg';
const FPS_ACTIVE = 30; // while the cursor is over the hero
const FPS_IDLE = 20; // slow cloud drift needs far fewer frames
const CELL = 3; // base grid pitch in CSS px
const MAX_CELLS = 120_000; // cap per-frame work on very wide / tall screens
const DOT = 1.5; // square size in CSS px
const COLOR: [number, number, number] = [176, 186, 214]; // soft lavender-blue

// No tilt or parallax — the scene stays still; the cursor only touches the particles
const TILT_X = 0;
const TILT_Y = 0;
const PARALLAX = 0;
const EASE_RATE = 1.8; // per second — lower is softer
const GLOW_FOLLOW = 2.2; // per second — the lens trails the cursor slowly
const GLOW_FADE = 1.5; // per second — fade in/out
const IMAGE_CENTER_Y = 0.62; // vertical position of the hands in the hero (0 top → 1 bottom)
const MOBILE_CENTER_Y = 0.6; // phones: hands sit between the headline and the bottom row
const MOBILE_WIDTH = 1.2; // phones: image spans 120% of the width — both hands visible, wrists cropped
const MOBILE_CELL = 2.5; // phones: finer grid so the smaller hands keep their detail

/** Hero backdrop: two reaching hands + cloud haze as a fine ordered-dither dot grid. */
export function ParticleFieldBackdrop({
  hoverMode = 'trail'
}: {
  hoverMode?: HoverMode;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const modeRef = useRef<HoverMode>(hoverMode);
  modeRef.current = hoverMode;
  useEffect(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!wrap || !canvas || !ctx) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const start = performance.now();
    let width = 0;
    let height = 0;
    let pageTop = 0;
    let pageLeft = 0;
    let scene: DitherScene | null = null;
    let frame = 0;
    let last = 0;
    let visible = true;
    const pointer = {
      x: 0,
      y: 0,
      vx: 0,
      vy: 0,
      active: false
    };
    let lastMove = {
      x: 0,
      y: 0,
      t: 0
    };
    // Normalised cursor position in the hero (-1..1), target and eased
    const tilt = {
      tx: 0,
      ty: 0,
      x: 0,
      y: 0
    };
    // Soft lens that trails the cursor slowly and fades in/out
    const glow = {
      x: 0,
      y: 0,
      strength: 0
    };
    const ripples: Ripple[] = [];
    let lastRipple = {
      x: -9999,
      y: -9999,
      t: 0
    };
    const img = new Image();
    img.crossOrigin = 'anonymous';
    const draw = (now: number) => {
      if (!scene) return;
      const t = (now - start) / 1000;
      renderDither(ctx, scene, {
        width,
        height,
        time: reduce ? 0 : t,
        intro: reduce ? 99 : t,
        color: COLOR,
        parallax: {
          x: tilt.x * PARALLAX,
          y: tilt.y * PARALLAX * 0.6
        },
        glow,
        mode: modeRef.current,
        ripples
      });
    };
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      width = canvas.offsetWidth;
      height = canvas.offsetHeight;
      // Wide screens: fewer device pixels to fill, and a grid that never exceeds MAX_CELLS
      const dpr = Math.min(window.devicePixelRatio || 1, width > 1600 ? 1 : 1.5);
      const base = width < 768 ? MOBILE_CELL : CELL;
      const cell = Math.max(base, Math.ceil(Math.sqrt(width * height / MAX_CELLS) * 2) / 2);
      const dot = DOT * (cell / CELL);
      pageTop = rect.top + window.scrollY;
      pageLeft = rect.left + window.scrollX;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      scene = createDitherScene(width, height, cell, dot, dpr);
      fitImage();
      draw(performance.now());
    };

    // Phones: show both hands whole and slightly smaller, sitting in the open space under the headline
    const fitImage = () => {
      if (!scene || !img.complete || !img.naturalWidth) return;
      if (width < 768) sampleImage(scene, img, MOBILE_CENTER_Y, {
        widthScale: MOBILE_WIDTH,
        minHeight: 0
      });else sampleImage(scene, img, IMAGE_CENTER_Y);
    };
    img.onload = () => {
      fitImage();
      draw(performance.now());
    };
    img.src = IMAGE_URL;
    const onMove = (e: PointerEvent) => {
      if (e.pointerType === 'touch') return;
      const x = e.clientX - (pageLeft - window.scrollX);
      const y = e.clientY - (pageTop - window.scrollY);
      const now = performance.now();
      const dt = Math.max(8, now - lastMove.t) / 1000;
      if (now - lastMove.t < 120) {
        pointer.vx = pointer.vx * 0.5 + (x - lastMove.x) / dt * 0.5;
        pointer.vy = pointer.vy * 0.5 + (y - lastMove.y) / dt * 0.5;
      } else {
        pointer.vx = 0;
        pointer.vy = 0;
      }
      lastMove = {
        x,
        y,
        t: now
      };
      pointer.x = x;
      pointer.y = y;
      pointer.active = x >= 0 && y >= 0 && x <= width && y <= height;
      if (pointer.active && modeRef.current === 'trail') {
        // Fill the gap since the last point so fast moves leave a continuous, direct trace
        const dist = Math.hypot(x - lastRipple.x, y - lastRipple.y);
        if (dist > 400) {
          ripples.push({
            x,
            y,
            age: 0
          });
        } else if (dist >= TRAIL_SPACING) {
          const steps = Math.min(8, Math.floor(dist / TRAIL_SPACING));
          for (let s = 1; s <= steps; s++) {
            const k = s / steps;
            ripples.push({
              x: lastRipple.x + (x - lastRipple.x) * k,
              y: lastRipple.y + (y - lastRipple.y) * k,
              age: 0
            });
          }
        }
        if (dist >= TRAIL_SPACING) lastRipple = {
          x,
          y,
          t: now
        };
        while (ripples.length > MAX_TRAIL) ripples.shift();
      } else if (pointer.active && modeRef.current === 'ripple' && now - lastRipple.t > RIPPLE_MIN_GAP && Math.hypot(x - lastRipple.x, y - lastRipple.y) > RIPPLE_SPACING) {
        ripples.push({
          x,
          y,
          age: 0
        });
        if (ripples.length > MAX_RIPPLES) ripples.shift();
        lastRipple = {
          x,
          y,
          t: now
        };
      }
      if (pointer.active) {
        tilt.tx = (x / width - 0.5) * 2;
        tilt.ty = (y / height - 0.5) * 2;
      } else {
        tilt.tx = 0;
        tilt.ty = 0;
      }
    };
    const onLeave = () => {
      pointer.active = false;
      tilt.tx = 0;
      tilt.ty = 0;
    };
    const loop = (now: number) => {
      frame = requestAnimationFrame(loop);
      const busy = pointer.active || ripples.length > 0 || glow.strength > 0.01;
      if (!visible || document.hidden || now - last < 1000 / (busy ? FPS_ACTIVE : FPS_IDLE)) return;
      const dt = Math.min(0.1, (now - (last || now)) / 1000);
      last = now;

      // Tilt is disabled (TILT_X/Y = 0) — skip per-frame transforms that force re-compositing
      if (TILT_X || TILT_Y) {
        const k = 1 - Math.exp(-EASE_RATE * dt);
        tilt.x += (tilt.tx - tilt.x) * k;
        tilt.y += (tilt.ty - tilt.y) * k;
        wrap.style.transform = `perspective(1400px) rotateX(${(-tilt.y * TILT_X).toFixed(3)}deg) rotateY(${(tilt.x * TILT_Y).toFixed(3)}deg) scale(1.04)`;
      }
      const kg = 1 - Math.exp(-GLOW_FOLLOW * dt);
      if (glow.strength < 0.01 && pointer.active) {
        glow.x = pointer.x;
        glow.y = pointer.y;
      }
      glow.x += (pointer.x - glow.x) * kg;
      glow.y += (pointer.y - glow.y) * kg;
      glow.strength += ((pointer.active ? 1 : 0) - glow.strength) * (1 - Math.exp(-GLOW_FADE * dt));
      for (let i = ripples.length - 1; i >= 0; i--) {
        ripples[i].age += dt;
        if (ripples[i].age > (modeRef.current === 'trail' ? TRAIL_LIFE : RIPPLE_LIFE)) ripples.splice(i, 1);
      }
      if (scene && modeRef.current === 'dissolve') {
        const moving = pointer.active && now - lastMove.t < 80;
        stepField(scene, {
          ...pointer,
          active: moving
        }, dt);
      }
      draw(now);
    };

    // Debounced: rebuilding the grid on every resize frame was a big source of jank
    let resizeTimer = 0;
    const ro = new ResizeObserver(() => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(resize, 150);
    });
    ro.observe(canvas);
    resize();
    const io = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
    });
    io.observe(canvas);
    if (!reduce) {
      window.addEventListener('pointermove', onMove, {
        passive: true
      });
      document.documentElement.addEventListener('pointerleave', onLeave);
      frame = requestAnimationFrame(loop);
    }
    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(resizeTimer);
      ro.disconnect();
      io.disconnect();
      window.removeEventListener('pointermove', onMove);
      document.documentElement.removeEventListener('pointerleave', onLeave);
    };
  }, []);
  return <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden" style={{
    maskImage: 'linear-gradient(to bottom, transparent 0%, #000 16%, #000 88%, transparent 100%)',
    WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, #000 16%, #000 88%, transparent 100%)'
  }} data-id="element-315" data-mp="147015tuhjza7">
      <div ref={wrapRef} className="absolute inset-0" data-id="element-316" data-mp="106pwqlmcdgxg">
        <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" style={{
        opacity: 0.6
      }} data-id="element-317" data-mp="l84bmvtko92p" />
      </div>
    </div>;
}